import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  asapScheduler,
  debounceTime,
  fromEvent,
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';

type InputValueType = string | number;

@Component({
  selector: 'app-input-box',
  templateUrl: 'input-box.component.html',
  styleUrls: ['input-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: InputBoxComponent,
    },
  ],
})
export class InputBoxComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('inputEl', { static: true })
  inputElRef: ElementRef<HTMLInputElement>;

  private formEl: HTMLFormElement;
  private formResetSubscription: Subscription;

  @Input()
  type: 'text' | 'number' | 'url' | 'password' = 'text';

  @Input()
  autocomplete = true;

  @Input()
  value: any;
  @Output()
  valueChange = new EventEmitter<any>();

  formControl: FormControl<string | number>;

  @Output()
  onReset = new EventEmitter<any>();
  @Output()
  onInput = new EventEmitter<any>();
  @Output()
  onFocus = new EventEmitter<any>();

  @Output()
  onKeydown = new EventEmitter<any>();
  @Output()
  onClearClick = new EventEmitter<any>();

  private onChange = (value) => {};

  private onTouched = () => {};

  @Input()
  name: string;

  @Input()
  placeholder: string;

  @Input()
  readOnly: boolean;

  @Input()
  get disabled() {
    return !!this.formControl.disabled;
  }

  set disabled(value) {
    value = !!value;
    if (value === this.disabled) {
      return;
    }

    if (value) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }

    this.changeDetector.detectChanges();
  }

  @Input()
  maxLength: number;
  @Input()
  max: number;

  @Input()
  min: number;

  @Input()
  showErrorEnabled: boolean = true;
  @Input()
  clearButtonEnabled: boolean;
  @Input()
  showClearButton: boolean = true;

  @Input()
  label: string;

  private _focused = false;
  get focused() {
    return this._focused;
  }

  set focused(value) {
    value = !!value;
    if (value !== this.focused) {
      this._focused = value;

      this.changeDetector.detectChanges();
    }
  }

  touched = false;

  get empty() {
    return !this.inputElRef?.nativeElement?.value && !this._isBadInput();
  }

  get isClearButtonVisible() {
    return !!(
      !this.disabled &&
      this.showClearButton &&
      (this.clearButtonEnabled || !this.readOnly) &&
      !!this.value
    );
  }

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef
  ) {
    this.formControl = this.formBuilder.control(null);
  }

  ngOnInit(): void {
    let parentEl = this.inputElRef.nativeElement.parentElement;
    while (parentEl && !(parentEl instanceof HTMLFormElement)) {
      parentEl = parentEl.parentElement;
    }

    if (parentEl instanceof HTMLFormElement) {
      this.formEl = parentEl;
      fromEvent(this.formEl, 'reset')
        .pipe(debounceTime(0, asapScheduler), takeUntil(this.lifecycle))
        .subscribe(() => {
          this.onInputChange();

          this.onReset.emit();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxLength) {
      if (typeof this.maxLength === 'number') {
        this.inputElRef.nativeElement.setAttribute(
          'maxLength',
          this.maxLength as any
        );
      } else {
        this.inputElRef.nativeElement.removeAttribute('maxLength');
      }
    }

    if (changes.max) {
      if (typeof this.max === 'number') {
        this.inputElRef.nativeElement.setAttribute('max', this.max as any);
      } else {
        this.inputElRef.nativeElement.removeAttribute('max');
      }
    }

    if (changes.min) {
      if (typeof this.min === 'number') {
        this.inputElRef.nativeElement.setAttribute('min', this.min as any);
      } else {
        this.inputElRef.nativeElement.removeAttribute('min');
      }
    }

    if (changes.value) {
      this._setInputValue();
    }
  }

  ngAfterViewInit(): void {
    this._setInputValue();
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  writeValue(value: InputValueType): void {
    if (this.value !== value) {
      this.value = value;
      this._setInputValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocusChanged(focus) {
    this.focused = focus;

    this.onFocus.emit(focus);
  }

  onInputChange() {
    this.formResetSubscription?.unsubscribe();

    let value = this._getValueFromInput();

    if (typeof value === 'number') {
      if (typeof this.max === 'number' && value > this.max) {
        value = this.max;
        this.formControl.reset(value);

        this.changeDetector.detectChanges();
      }

      if (typeof this.min === 'number' && value < this.min) {
        value = this.min;
        this.formControl.reset(value);

        this.changeDetector.detectChanges();
      }
    }

    if (this.value !== value) {
      this.value = value;

      this.valueChange.emit(value);
      this.onChange?.(value);
    }
  }

  onInputValue(event) {
    this.onInputChange();

    this.onInput.emit({ event, component: this });
  }

  onInputKeydown(event) {
    this.onKeydown.emit({ event, component: this });
  }

  onBtnClearClick(e) {
    e?.stopPropagation();
    this.onClearClick.emit();

    this.formControl.reset();
    this.onInputChange();
  }

  @HostListener('click')
  onContainerClick() {
    if (!this.focused && !this.disabled && !this.readOnly) {
      this.focused = true;
      this.inputElRef.nativeElement.focus();

      if (!this.touched) {
        this.touched = true;
        this.onTouched?.();
      }
    }
  }

  protected _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let validity = (this.inputElRef?.nativeElement as HTMLInputElement)
      ?.validity;
    return validity && validity.badInput;
  }

  private _getValueFromInput() {
    const inputEl = this.inputElRef.nativeElement;

    switch (this.type) {
      case 'number':
        return inputEl.valueAsNumber;
      case 'text':
      default:
        return inputEl.value;
    }
  }

  private _setInputValue() {
    this.formControl.reset(this.value);

    this.changeDetector.detectChanges();
  }
}
